import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { formatDistance, unpackToDateTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { Delete as DeleteIcon, HowToRegRounded, KeyboardArrowLeft, Redo } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import React, { useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { DateTime } from 'luxon';
import { StatusChip } from '../../../../common/chips/StatusChip';
import { getIncomingStatusChips, isAwaitingScreeningReview } from '../common/incoming-volunteer-status';
import { ConfirmationDialog } from '../../../../common/dialogs/ConfirmationDialog';
import { SnackbarContextProps } from '../../../../global/config/SnackbarContext';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../global/network/useEndpointFetch';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FallbackAvatar } from '../../../general/user-profile/components/Avatar';
import { BorderLinearProgress } from '../incoming-volunteers/IncomingVolunteersScreenSimple';
import { IncomingVolunteersApplicationTaskSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteersApplicationTaskSection';
import { IncomingVolunteersInviteTaskSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteersInviteTaskSection';
import { IncomingVolunteerTasksSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteerTasksSection';
import { IncomingVolunteerConfirmationDialog } from './components/IncomingVolunteerConfirmationDialog';
import { TaskSideMenuBar } from './components/side-menu-content/TaskSideMenuBar';
import { IncomingVolunteersLeftColumnWrapper } from './components/wrappers/IncomingVolunteersLeftColumnWrapper';
import { IncomingVolunteersPageWrapper } from './components/wrappers/IncomingVolunteersPageWrapper';
import { IncomingVolunteersRightColumnWrapper } from './components/wrappers/IncomingVolunteersRightColumnWrapper';
import { useIncomingVolunteerActions } from './incoming-volunteer-action.gql';
import { GET_INCOMING_VOLUNTEER_PROFILE, LeftMenuItemType } from './incoming-volunteer-model.gql';
import {
  GetIncomingVolunteerProfile,
  GetIncomingVolunteerProfileVariables,
} from './__generated__/GetIncomingVolunteerProfile';
import { useVolunteersContext } from '../VolunteersContext';
import { CommentsList } from '../common/CommentsList';
import { BypassDialog } from './BypassDialog';
import { MatchingPanel, MatchedCandidate } from './components/matching/MatchingPanel';
import { useMatchingRules } from './components/matching/use-matching-rules';
import { ApplicationActionsDialog } from './components/ApplicationActionsDialog';
import { GET_APPLICATION_CONFIG } from '../../../admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/admin-console-applicant-screening.gql';
import { GetApplicationConfig } from '../../../admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationConfig';
import { IncomingVolunteerRolesSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteerRolesSection';
import { FunnelIndicators } from './components/FunnelIndicators';
import { CommunicateButton } from '../../volunteer-common-profile/profile-sections/action-menu/CommunicateButton';
import { ApplicantCommunicationsSection } from './components/ApplicantCommunicationsSection';
import { ExpandableSidePanel } from './components/ExpandableSidePanel';
import { IncomingVolunteerActivitiesSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteerActivitiesSection';

const useStyles = () => {
  const { theme, isMobile } = useCampfireTheme();
  return makeStyles({
    listItem: {
      padding: isMobile ? '16px 8px' : '16px 48px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    iconContainer: {
      marginRight: 8,
    },
    canvas: {
      backgroundColor: theme.color.grey.background,
      padding: isMobile ? '10px 8px' : '16px 20px',
      flexGrow: 1,
      flexShrink: 0,
      minWidth: 0,
      minHeight: '100%',
      boxSizing: 'border-box',
      display: 'flex',
    },
    card: {
      backgroundColor: '#ffffff',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: 10,
      overflow: 'hidden',
      padding: '6px 0 2px',
      height: '100%',
      boxSizing: 'border-box',
    },
    taskMenuBarWrapper: {
      borderLeft: `1px solid ${theme.color.grey.border}`,
      borderTop: `1px solid ${theme.color.grey.border}`,
      boxShadow: '0px 5px 8px rgba(114, 114, 114, 0.16), 0px 7px 14px rgba(214, 214, 214, 0.26)',
    },
  })();
};

const IncomingVolunteerViewProfileScreen = ({
  profileId,
  goBack,
  reloadList,
  setSnackbar,
}: {
  profileId: string;
  goBack: () => void;
  reloadList?: () => void;
  setSnackbar: (props: SnackbarContextProps | undefined) => void;
}) => {
  const [leftMenuItem, setLeftMenuItem] = useState<LeftMenuItemType>();
  const [openApproveProfileDialog, setOpenApproveProfileDialog] = useState(false);
  const [openMatchActionsDialog, setOpenMatchActionsDialog] = useState(false);
  const [matchedCandidate, setMatchedCandidate] = useState<MatchedCandidate | null>(null);
  const [selectedVolunteerProfileId] = useQueryParam('volunteerProfileId', StringParam);
  const classes = useStyles();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { runRemoveApplicant, runRemoveApplicantLoading } = useIncomingVolunteerActions();
  const completeVolunteerProfile = useEndpointFetch<{ profileId: string; bypass?: boolean }>(
    '/vm/volunteer/profile/complete'
  );
  const { styles, isMobile, isSm } = useCampfireTheme();

  const { data, loading, refetch } = useCampfireQuery<
    GetIncomingVolunteerProfile,
    GetIncomingVolunteerProfileVariables
  >(GET_INCOMING_VOLUNTEER_PROFILE, {
    options: {
      variables: {
        profileId,
        from: DateTime.local().toISODate(),
        until: DateTime.local()
          .plus({ years: 1 })
          .toISODate(),
      },
    },
  });

  const { data: configData } = useCampfireQuery<GetApplicationConfig, undefined>(GET_APPLICATION_CONFIG);

  const applicationAvailabilitySimple = configData?.vm.availabilitySimple?.configItemValue?.flagValue ?? false;
  const applicationAvailabilityComplex = configData?.vm.availabilityComplex?.configItemValue?.flagValue ?? false;
  const applicationActivityEnrolment = configData?.vm.enrolActivity?.configItemValue?.flagValue ?? false;
  const applicationApplyRoles = configData?.vm.applyRole?.configItemValue?.flagValue ?? false;

  const profile = useMemo(() => {
    return data?.vm.profile ?? undefined;
  }, [data]);

  const lastUpdatedDateTime = unpackToDateTime(profile?.dateLastUpdated);
  const now = DateTime.local();
  const lastUpdated = lastUpdatedDateTime.hasSame(now, 'day')
    ? 'today'
    : lastUpdatedDateTime.hasSame(now.minus({ days: 1 }), 'day')
    ? 'yesterday'
    : lastUpdatedDateTime.toFormat('d MMMM, yyyy');

  const allCompletedTasks = useMemo(() => {
    return profile?.completedTasks ?? [];
  }, [profile]);

  const isPendingApproval = useMemo(() => {
    const completedTasks =
      data?.vm?.profile?.completedTasks.filter(
        (x) => x.task.cake.cakeType === 'required' && (x.status === 'approved' || x.status === 'admin-approved')
      ) ?? [];

    const requiredTasks =
      data?.vm?.profile?.application?.relevantTasks.filter((x) => x.cake.cakeType === 'required') ?? [];

    const requiredCompletedTasks =
      requiredTasks.filter(
        (requiredTask) => completedTasks.find((ct) => ct.task.taskId === requiredTask.taskId) !== undefined
      ) ?? [];

    const activityApplications =
      data?.vm?.profile?.application?.applicationActivities.filter((x) => x.status === 'pending') ?? [];

    const roleApplications =
      data?.vm?.profile?.application?.applicationRoles.filter((x) => x.status === 'pending') ?? [];

    return (
      requiredCompletedTasks.length === requiredTasks.length &&
      !!requiredTasks.length &&
      !!requiredCompletedTasks.length &&
      roleApplications.length === 0 &&
      activityApplications.length === 0
    );
  }, [data]);

  const application = profile?.application;

  const { findRuleForRoles } = useMatchingRules();

  const matchingRuleMatch = useMemo(() => findRuleForRoles(application?.applicationRoles), [
    findRuleForRoles,
    application,
  ]);

  const funnel = application?.funnel;
  const funnelHasRoles = (funnel?.activityRoles?.length ?? 0) > 0;
  const funnelHasActivities = (funnel?.activities?.length ?? 0) > 0;
  const invitation = application?.invitee;
  const isProfilePending = allCompletedTasks.find(
    (x) => x.task.cake.cakeType === 'required' && x.status === 'pending' && !x.task.dateRemoved
  );
  const isProfileApproved = profile?.isCompleted && profile?.isProfileTaskApproved;
  const isApplicationApproved = application?.status === 'approved';
  const statusChips = getIncomingStatusChips({
    applicationStatus: application?.status,
    profileId: profile?.profileId,
    inviteeId: invitation?.inviteeId,
    dateRevoked: invitation?.dateRevoked,
    isCompleted: profile?.isCompleted,
    isProfileTaskApproved: profile?.isProfileTaskApproved,
    hasPendingTasks: Boolean(isProfilePending),
    dateRemoved: profile?.dateRemoved,
  });
  const [openBypass, setOpenBypass] = React.useState(false);
  const onBypass = () => {
    if (selectedVolunteerProfileId) {
      completeVolunteerProfile
        .run({
          profileId: selectedVolunteerProfileId,
          bypass: true,
        })
        .then(() => {
          if (reloadList) reloadList();
          goBack();
        });
    }
  };
  const isApplicationRemoved = application?.status === 'rejected' || application?.status === 'removed';
  const isProfileRemoved = Boolean(profile?.dateRemoved);
  const [communicationsReloadSignal, setCommunicationsReloadSignal] = useState(0);

  const sidebarContent = application && (
    <Box width='100%' display='flex' flexDirection='column' flexGrow={1} style={{ gap: '1rem', minHeight: 0 }}>
      <ExpandableSidePanel
        fill
        expandable
        title='Comments'
        subtitle={`Comments left on ${profile?.preferredName ?? ''}'s application`}
      >
        <CommentsList applicationId={application.applicationId} collapsibleComposer />
      </ExpandableSidePanel>
      <ApplicantCommunicationsSection
        userId={profile?.userId}
        applicantName={profile?.preferredName ?? ''}
        reminders={application.applicationReminders ?? []}
        reloadSignal={communicationsReloadSignal}
      />
    </Box>
  );

  return (
    <IncomingVolunteersPageWrapper>
      <ConfirmationDialog
        title={'Confirm to remove applicant?'}
        body={
          <>
            <Typography variant='body1' gutterBottom>
              {`All onboarding progress will be removed for the applicant and their account will be deactivated.`}
            </Typography>
            <Typography variant='body1'>
              {'Removed applicants will receive an email notifying them that their application has been unsuccessful.'}
            </Typography>
          </>
        }
        open={openConfirmation}
        closeActionText={'Cancel'}
        confirmActionText={'Remove Applicant'}
        handleCloseClick={() => setOpenConfirmation(false)}
        handleConfirmClick={() => {
          runRemoveApplicant({
            applicationId: application?.applicationId ?? '',
            status: 'removed',
          }).then(() => {
            if (reloadList) reloadList();
            goBack();
          });
        }}
      />

      <IncomingVolunteerConfirmationDialog
        title='Approve Volunteer Profile?'
        body={`This volunteer has finished all onboarding tasks. Approving this task will activate their Volaby account. They'll receive an email inviting them to start looking for activities to join. If this user has applied for roles or activities, they will be added to this automatically.`}
        warningTitle={matchingRuleMatch && !matchedCandidate ? 'No match selected' : undefined}
        warning={
          matchingRuleMatch && !matchedCandidate
            ? `${matchingRuleMatch.rule.name} applies to this applicant. Approving now will not pair them with a ${matchingRuleMatch.suggestedRole.name}. Choose a match under Find a Match first if you want the paired activity created.`
            : undefined
        }
        open={openApproveProfileDialog}
        id={selectedVolunteerProfileId ?? ''}
        disabled={completeVolunteerProfile.isLoading}
        handleCloseClick={() => setOpenApproveProfileDialog(false)}
        approveButtonText='Approve Volunteer Profile'
        handleApproveClick={() => {
          if (selectedVolunteerProfileId) {
            completeVolunteerProfile.run({ profileId: selectedVolunteerProfileId }).then((res) => {
              if (res.ok) {
                setSnackbar({
                  open: true,
                  message: 'Volunteer Profile Approved',
                  variant: 'success',
                });
                if (reloadList) {
                  reloadList();
                  goBack();
                  setOpenApproveProfileDialog(false);
                }
                return;
              }
              setSnackbar({
                open: true,
                message: 'Cannot approve volunteer profile',
                variant: 'error',
              });
            });
          } else {
            setSnackbar({
              open: true,
              message: 'Cannot approve volunteer profile',
              variant: 'error',
            });
          }
        }}
      />

      <CircularProgressOverlay isLoading={loading || runRemoveApplicantLoading} />
      <IncomingVolunteersLeftColumnWrapper rightColumnOpen={!!leftMenuItem}>
        {profile && (
          <Grid item container direction='row' wrap='nowrap' alignItems='center' className={classes.listItem}>
            <Grid item>
              <IconButton size='small' onClick={() => goBack()} style={{ padding: 6 }}>
                <KeyboardArrowLeft fontSize='small' />
              </IconButton>
            </Grid>
            <Grid item className={classes.iconContainer}>
              <FallbackAvatar
                initials={`${profile.preferredName} ${profile.lastName}`
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
                size={32}
              />
            </Grid>
            <Grid
              item
              container
              direction={isMobile ? 'column' : 'row'}
              wrap={isMobile ? 'wrap' : 'nowrap'}
              spacing={isMobile ? 1 : 0}
              alignItems='center'
            >
              <Grid item container direction='column' spacing={isSm ? 1 : 0}>
                <Grid item container direction={isMobile ? 'column' : 'row'}>
                  <Grid item>
                    <Typography display='inline' style={{ marginRight: 8 }}>
                      {`${profile.preferredName} ${profile.lastName} `}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography display='inline' variant='body2' color='textSecondary'>
                      {profile.email}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container direction='row' alignItems='center' spacing={1}>
                  {statusChips.map((chip) => (
                    <Grid item key={chip.label}>
                      <StatusChip tiny status={chip.label} bgcolor={chip.color} />
                    </Grid>
                  ))}
                  {!!profile.percentageComplete && (
                    <Grid item style={{ width: 250 }}>
                      <BorderLinearProgress variant='determinate' value={profile.percentageComplete * 100} />
                    </Grid>
                  )}
                  <Grid item>
                    <Typography variant='caption' color='textSecondary'>
                      {`Last updated ${lastUpdated}`}
                    </Typography>
                  </Grid>
                  {application?.mostRecentReminder && (
                    <Grid item>
                      <Typography variant='caption' color='textSecondary'>
                        {`Reminded ${formatDistance(unpackToDateTime(application.mostRecentReminder.remindedAt))}`}
                      </Typography>
                    </Grid>
                  )}
                  {funnel && (
                    <Grid item style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography variant='caption' color='textSecondary'>{`Funnel: ${funnel.name}`}</Typography>
                      <FunnelIndicators funnel={funnel} />
                    </Grid>
                  )}
                </Grid>
                <Grid item className={styles.xsOnly}>
                  <IncomingVolunteerViewProfileActionMenu
                    setOpenApproveProfileDialog={setOpenApproveProfileDialog}
                    setOpenConfirmation={setOpenConfirmation}
                    isPendingApproval={isPendingApproval}
                    leftMenuItem={leftMenuItem}
                    application={application}
                    setOpenBypass={setOpenBypass}
                    isAccountRemoved={isApplicationRemoved || isProfileRemoved}
                    matchedCandidate={matchedCandidate}
                    setOpenMatchActionsDialog={setOpenMatchActionsDialog}
                    profile={profile}
                    onCommunicationSent={() => setCommunicationsReloadSignal((x) => x + 1)}
                  />
                </Grid>
              </Grid>
              <Box className={styles.notXs}>
                <IncomingVolunteerViewProfileActionMenu
                  setOpenApproveProfileDialog={setOpenApproveProfileDialog}
                  setOpenConfirmation={setOpenConfirmation}
                  isPendingApproval={isPendingApproval}
                  leftMenuItem={leftMenuItem}
                  application={application}
                  setOpenBypass={setOpenBypass}
                  isAccountRemoved={isApplicationRemoved || isProfileRemoved}
                  matchedCandidate={matchedCandidate}
                  setOpenMatchActionsDialog={setOpenMatchActionsDialog}
                  profile={profile}
                  onCommunicationSent={() => setCommunicationsReloadSignal((x) => x + 1)}
                />
              </Box>
            </Grid>
          </Grid>
        )}
        <Box className={classes.canvas}>
          <Grid container spacing={2} alignItems='stretch' style={{ flexGrow: 1 }}>
            <Grid item xs={12} md={!leftMenuItem && application ? 8 : 12}>
              <Box className={classes.card}>
                {invitation ? (
                  <IncomingVolunteersInviteTaskSection
                    subText={`Invited by ${invitation.invitedBy.profile.preferredName} ${invitation.invitedBy.profile.lastName}`}
                    status={`Invited ${formatDistance(unpackToDateTime(invitation.dateInvited))}`}
                    onClick={() => setLeftMenuItem({ inviteeId: invitation.inviteeId, type: invitation.__typename })}
                  />
                ) : null}

                {application ? (
                  <IncomingVolunteersApplicationTaskSection
                    status={isApplicationApproved ? 'approved' : 'pending'}
                    taskRecentActivityStatus={`Submitted ${formatDistance(
                      unpackToDateTime(application.dateSubmitted)
                    )}`}
                    onClick={() =>
                      setLeftMenuItem({
                        applicationId: application.applicationId,
                        type: application.__typename,
                      })
                    }
                  />
                ) : null}

                {profile && (
                  <IncomingVolunteerTasksSection
                    selectedTaskId={
                      leftMenuItem?.type === 'VOLUNTEER_PermissionlessTaskType' ? leftMenuItem.taskId : undefined
                    }
                    setLeftMenuItem={setLeftMenuItem}
                    completedTasks={allCompletedTasks}
                    applicationId={application?.applicationId}
                    currentStageId={application?.currentStageId}
                    relevantTasks={application?.relevantTasks}
                    refetch={refetch}
                  />
                )}

                {(applicationApplyRoles || funnelHasRoles) && (
                  <IncomingVolunteerRolesSection
                    setLeftMenuItem={setLeftMenuItem}
                    applicationRoles={application?.applicationRoles}
                    selectedApplicationRoleId={
                      leftMenuItem?.type === 'VOLUNTEER_ApplicationRoleType'
                        ? leftMenuItem.applicationRoleId
                        : undefined
                    }
                    userId={isProfileApproved ? profile?.userId : undefined}
                    refetch={isProfileApproved ? refetch : undefined}
                  />
                )}
                {(applicationActivityEnrolment ||
                  applicationAvailabilityComplex ||
                  applicationAvailabilitySimple ||
                  funnelHasActivities) && (
                  <IncomingVolunteerActivitiesSection
                    setLeftMenuItem={setLeftMenuItem}
                    applicationActivities={application?.applicationActivities}
                    applicationAvailabilitySimple={applicationAvailabilitySimple}
                    selectedApplicationActivityId={
                      leftMenuItem?.type === 'VOLUNTEER_ApplicationActivityType'
                        ? leftMenuItem.applicationActivityId
                        : undefined
                    }
                    availabilitySelected={leftMenuItem?.type === 'VOLUNTEER_SimpleAvailabilityType'}
                    userId={isProfileApproved ? profile?.userId : undefined}
                    refetch={isProfileApproved ? refetch : undefined}
                  />
                )}
                {matchingRuleMatch && application && profile?.profileId && (
                  <MatchingPanel
                    applicantName={`${profile?.preferredName ?? ''} ${profile?.lastName ?? ''}`}
                    applicantRoleName={matchingRuleMatch.matchedRole.name}
                    applicantProfileId={profile.profileId}
                    counterpartRoleId={matchingRuleMatch.suggestedRole.activityRoleId}
                    counterpartRoleName={matchingRuleMatch.suggestedRole.name}
                    ruleProgramId={matchingRuleMatch.rule.program?.programId ?? null}
                    minimumScore={matchingRuleMatch.rule.minimumScore}
                    onMatchSelect={setMatchedCandidate}
                  />
                )}
              </Box>
            </Grid>
            {!leftMenuItem && application && (
              <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                {sidebarContent}
              </Grid>
            )}
          </Grid>
        </Box>
      </IncomingVolunteersLeftColumnWrapper>

      {leftMenuItem ? (
        <IncomingVolunteersRightColumnWrapper>
          <TaskSideMenuBar
            selectedUserId={profile?.userId}
            onClose={() => setLeftMenuItem(undefined)}
            leftMenuItem={leftMenuItem}
            reload={() => {
              refetch();
            }}
            reloadList={reloadList}
            setSnackbar={setSnackbar}
            roleApplications={application?.applicationRoles}
            activityApplications={application?.applicationActivities}
          />
        </IncomingVolunteersRightColumnWrapper>
      ) : null}
      {matchedCandidate && matchingRuleMatch && (
        <ApplicationActionsDialog
          open={openMatchActionsDialog}
          onClose={() => setOpenMatchActionsDialog(false)}
          profileId={profileId}
          applicantName={`${profile?.preferredName ?? ''} ${profile?.lastName ?? ''}`}
          applicantRoleId={matchingRuleMatch.matchedRole.activityRoleId}
          rule={matchingRuleMatch.rule}
          applicantPrograms={profile?.programs ?? []}
          counterpartRoleId={matchingRuleMatch.rule.counterpartRole.activityRoleId}
          matchedCandidate={matchedCandidate}
          onSuccess={() => {
            setSnackbar({
              open: true,
              message: 'Match created and volunteer profile approved',
              variant: 'success',
            });
            if (reloadList) {
              reloadList();
              goBack();
            }
          }}
        />
      )}
      <BypassDialog
        open={openBypass}
        onClose={() => setOpenBypass(false)}
        onCancel={() => setOpenBypass(false)}
        onBypass={onBypass}
      />
    </IncomingVolunteersPageWrapper>
  );
};

const IncomingVolunteerViewProfileActionMenu = (props: {
  setOpenApproveProfileDialog: (x: boolean) => void;
  setOpenConfirmation: (x: boolean) => void;
  isPendingApproval: boolean;
  leftMenuItem?: any;
  application?: any;
  setOpenBypass: React.Dispatch<React.SetStateAction<boolean>>;
  isAccountRemoved?: boolean;
  matchedCandidate?: MatchedCandidate | null;
  setOpenMatchActionsDialog?: (x: boolean) => void;
  profile?: any;
  onCommunicationSent?: () => void;
}) => {
  const {
    setOpenApproveProfileDialog,
    setOpenConfirmation,
    isPendingApproval,
    leftMenuItem,
    application,
    setOpenBypass,
    isAccountRemoved,
    matchedCandidate,
    setOpenMatchActionsDialog,
    profile,
    onCommunicationSent,
  } = props;

  const { isMobile } = useCampfireTheme();
  const { setRemindDialog, setApplicationsReminder } = useVolunteersContext();

  const onClickSendReminder = () => {
    setApplicationsReminder([application]);
    setRemindDialog(true);
  };

  return !leftMenuItem ? (
    <Grid
      item
      container
      style={{
        display: 'flex',
        gap: '8px',
        minWidth: 'auto',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
      }}
      justify={isMobile ? 'flex-start' : 'flex-end'}
    >
      <Box display='flex' style={{ gap: '12px' }}>
        {profile && !isAccountRemoved && (
          <CommunicateButton
            profile={{
              profileId: profile.profileId,
              preferredName: profile.preferredName,
              lastName: profile.lastName,
              contactNumber: profile.contactNumber,
              avatarUrl: profile.avatarUrl,
            }}
            dataTrack='fs-incVol-profile-contact-button'
            buttonStyle={{ marginRight: 0 }}
            onSendReminder={
              application && !isPendingApproval && !isAwaitingScreeningReview(application.status)
                ? onClickSendReminder
                : undefined
            }
            onSent={onCommunicationSent}
          />
        )}
        {isPendingApproval ? (
          <TabletButton
            color='primary'
            variant='outlined'
            style={{ borderRadius: 4, padding: '0.625rem 1.125rem', whiteSpace: 'nowrap' }}
            onClick={() => {
              if (matchedCandidate && setOpenMatchActionsDialog) {
                setOpenMatchActionsDialog(true);
              } else {
                setOpenApproveProfileDialog(true);
              }
            }}
            disabled={isAccountRemoved}
          >
            {isMobile ? null : <HowToRegRounded color='primary' style={{ fontSize: 14 }} />}
            <Typography noWrap style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
              {isMobile ? 'Approve' : 'Approve Profile'}
            </Typography>
          </TabletButton>
        ) : (
          <TabletButton
            data-track='fs-incVol-invite-volunteers-button'
            variant='outlined'
            onClick={() => setOpenBypass(true)}
            style={{ borderRadius: 4, padding: '0.625rem 1.125rem', whiteSpace: 'nowrap' }}
            disabled={isAccountRemoved}
          >
            <Redo color='inherit' style={{ fontSize: 14 }} />
            <Typography noWrap style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
              Bypass
            </Typography>
          </TabletButton>
        )}
      </Box>
      {!isAccountRemoved && (
        <Grid item>
          <TabletButton
            color='error'
            variant='outlined'
            onClick={() => setOpenConfirmation(true)}
            style={{ borderRadius: 4, padding: '0.625rem 1.125rem', whiteSpace: 'nowrap' }}
          >
            {isMobile ? null : <DeleteIcon style={{ fontSize: 14 }} />}
            <Typography noWrap style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
              {isMobile ? 'Remove' : 'Remove Applicant'}
            </Typography>
          </TabletButton>
        </Grid>
      )}
    </Grid>
  ) : null;
};

export { IncomingVolunteerViewProfileScreen };
